import Phaser from 'phaser';
import { Raver } from './Raver';
import { RaverState } from './RaverAI';
import { IsoGrid } from '@/iso/IsoGrid';
import { SoundCoverageMap } from './SoundCoverageMap';
import { LightCoverageMap } from './LightCoverageMap';
import { DrinkAccessMap } from './DrinkAccessMap';
import { SatisfactionCalculator, CellSatisfaction } from './SatisfactionCalculator';
import { NEON_PALETTE } from '@/utils/ColorUtils';
import { GRID_COLS, GRID_ROWS, EquipmentType, EQUIPMENT_DEFS, DRINK_PRICE, TICKET_PRICE } from '@/config';
import { EventBus, GameEvent } from '@/utils/EventBus';

interface EquipmentInfo {
  type: EquipmentType;
  col: number;
  row: number;
}

/**
 * Manages raver spawning, despawning, movement, and sprite rendering.
 */
export class CrowdManager {
  private scene: Phaser.Scene;
  private grid: IsoGrid;
  private ravers: Map<string, Raver> = new Map();
  private sprites: Map<string, Phaser.GameObjects.Image> = new Map();
  private nextRaverId = 0;

  private soundMap: SoundCoverageMap;
  private lightMap: LightCoverageMap;
  private drinkMap: DrinkAccessMap;

  private _venueSatisfaction = 0;
  private hasDJBooth = false;
  private barPositions: Array<{ col: number; row: number }> = [];

  constructor(scene: Phaser.Scene, grid: IsoGrid) {
    this.scene = scene;
    this.grid = grid;
    this.soundMap = new SoundCoverageMap(GRID_COLS, GRID_ROWS);
    this.lightMap = new LightCoverageMap(GRID_COLS, GRID_ROWS);
    this.drinkMap = new DrinkAccessMap(GRID_COLS, GRID_ROWS);
  }

  get venueSatisfaction(): number {
    return this._venueSatisfaction;
  }

  get raverCount(): number {
    return this.ravers.size;
  }

  /** Get all ravers for state snapshots */
  getRavers(): Raver[] {
    return Array.from(this.ravers.values());
  }

  /** Recalculate coverage maps when equipment changes */
  refreshCoverage(equipment: EquipmentInfo[]): void {
    const speakers = equipment
      .filter((e) => e.type === EquipmentType.SPEAKER)
      .map((e) => ({ col: e.col, row: e.row, radius: EQUIPMENT_DEFS[EquipmentType.SPEAKER].radius }));
    this.soundMap.recalculate(speakers);

    const lights = equipment
      .filter((e) => e.type === EquipmentType.LASER || e.type === EquipmentType.STROBE)
      .map((e) => ({ col: e.col, row: e.row, radius: EQUIPMENT_DEFS[e.type].radius }));
    this.lightMap.recalculate(lights);

    this.barPositions = equipment
      .filter((e) => e.type === EquipmentType.BAR)
      .map((e) => ({ col: e.col, row: e.row }));
    this.drinkMap.recalculate(this.barPositions);

    this.hasDJBooth = equipment.some((e) => e.type === EquipmentType.DJ_BOOTH);

    EventBus.emit(GameEvent.COVERAGE_UPDATED);
  }

  /** Main simulation tick */
  tick(economy: { addIncome: (n: number) => void }): void {
    // Spawn new ravers if DJ booth exists and satisfaction is decent
    if (this.hasDJBooth && this.ravers.size < 50) {
      const spawnChance = 0.1 + this._venueSatisfaction * 0.3;
      if (Math.random() < spawnChance) {
        this.spawnRaver(economy);
      }
    }

    // Tick each raver
    const satisfactions: CellSatisfaction[] = [];
    const toRemove: string[] = [];

    for (const [id, raver] of this.ravers) {
      const sound = this.soundMap.getCoverage(raver.col, raver.row);
      const light = this.lightMap.getCoverage(raver.col, raver.row);
      const drinks = this.drinkMap.getCoverage(raver.col, raver.row);
      const space = this.getSpaceFactor(raver.col, raver.row);

      const cellSat: CellSatisfaction = { sound, light, drinks, space };
      const satValue = SatisfactionCalculator.calculate(cellSat);

      // Handle bar-related states
      const nearBar = this.isNearBar(raver.col, raver.row);
      const atBar = this.isAtBar(raver.col, raver.row);

      // If raver just became thirsty, set target to nearest bar
      if (raver.state === RaverState.THIRSTY || raver.state === RaverState.GOING_TO_BAR) {
        const nearestBar = this.findNearestBar(raver.col, raver.row);
        if (nearestBar) {
          raver.setTarget(nearestBar.col, nearestBar.row);
        }
      }

      // If returning from bar, set target back to a dance spot
      if (raver.state === RaverState.RETURNING && raver.isAtTarget) {
        const spot = this.findDanceSpot();
        if (spot) raver.setTarget(spot.col, spot.row);
      }

      // Drink purchased
      const prevDrinks = raver.drinksPurchased;
      raver.tick(satValue, nearBar, atBar);
      if (raver.drinksPurchased > prevDrinks) {
        economy.addIncome(DRINK_PRICE);
      }

      if (raver.isLeaving) {
        toRemove.push(id);
      }

      satisfactions.push(cellSat);
    }

    // Remove leaving ravers
    for (const id of toRemove) {
      this.removeRaver(id);
    }

    // Update venue satisfaction
    this._venueSatisfaction = SatisfactionCalculator.venueAverage(satisfactions);
    EventBus.emit(GameEvent.SATISFACTION_CHANGED, this._venueSatisfaction);
    EventBus.emit(GameEvent.RAVER_COUNT_CHANGED, this.ravers.size);

    // Update sprite positions
    this.updateSprites();
  }

  private spawnRaver(economy: { addIncome: (n: number) => void }): void {
    const id = `raver-${this.nextRaverId++}`;
    const colorIdx = this.nextRaverId % NEON_PALETTE.length;

    // Enter from a random edge
    const edge = Math.floor(Math.random() * 4);
    let entryCol: number, entryRow: number;
    switch (edge) {
      case 0: entryCol = 0; entryRow = Math.floor(Math.random() * GRID_ROWS); break;
      case 1: entryCol = GRID_COLS - 1; entryRow = Math.floor(Math.random() * GRID_ROWS); break;
      case 2: entryCol = Math.floor(Math.random() * GRID_COLS); entryRow = 0; break;
      default: entryCol = Math.floor(Math.random() * GRID_COLS); entryRow = GRID_ROWS - 1; break;
    }

    const raver = new Raver(id, entryCol, entryRow, colorIdx);

    // Find a dance spot
    const spot = this.findDanceSpot();
    if (spot) {
      raver.setTarget(spot.col, spot.row);
    }

    this.ravers.set(id, raver);

    // Create sprite
    const world = this.grid.gridToWorld(entryCol, entryRow);
    const sprite = this.scene.add.image(world.x, world.y, `raver-${colorIdx}`);
    sprite.setDepth(world.y + 5);
    this.sprites.set(id, sprite);

    // Ticket income
    economy.addIncome(TICKET_PRICE);
  }

  private removeRaver(id: string): void {
    this.ravers.delete(id);
    const sprite = this.sprites.get(id);
    if (sprite) {
      sprite.destroy();
      this.sprites.delete(id);
    }
  }

  private updateSprites(): void {
    for (const [id, raver] of this.ravers) {
      const sprite = this.sprites.get(id);
      if (!sprite) continue;

      const world = this.grid.gridToWorld(raver.col, raver.row);
      sprite.setPosition(world.x, world.y);
      sprite.setDepth(world.y + 5);

      // Dancing bob animation
      if (raver.state === RaverState.DANCING) {
        const bob = Math.sin(Date.now() * 0.01 + raver.col) * 2;
        sprite.setPosition(world.x, world.y + bob);
      }
    }
  }

  private findDanceSpot(): { col: number; row: number } | null {
    // Find cells with good sound coverage that aren't occupied
    let bestCol = Math.floor(GRID_COLS / 2);
    let bestRow = Math.floor(GRID_ROWS / 2);
    let bestScore = -1;

    for (let attempts = 0; attempts < 20; attempts++) {
      const col = Math.floor(Math.random() * GRID_COLS);
      const row = Math.floor(Math.random() * GRID_ROWS);

      if (this.grid.isCellOccupied(col, row)) continue;

      const score = this.soundMap.getCoverage(col, row) + this.lightMap.getCoverage(col, row) * 0.5;
      if (score > bestScore) {
        bestScore = score;
        bestCol = col;
        bestRow = row;
      }
    }

    return { col: bestCol, row: bestRow };
  }

  private findNearestBar(col: number, row: number): { col: number; row: number } | null {
    if (this.barPositions.length === 0) return null;

    let nearest = this.barPositions[0];
    let minDist = Math.abs(col - nearest.col) + Math.abs(row - nearest.row);

    for (const bar of this.barPositions) {
      const dist = Math.abs(col - bar.col) + Math.abs(row - bar.row);
      if (dist < minDist) {
        minDist = dist;
        nearest = bar;
      }
    }

    return nearest;
  }

  private isNearBar(col: number, row: number): boolean {
    return this.barPositions.some(
      (bar) => Math.abs(col - bar.col) + Math.abs(row - bar.row) <= 2
    );
  }

  private isAtBar(col: number, row: number): boolean {
    return this.barPositions.some(
      (bar) => col === bar.col && row === bar.row
    );
  }

  private getSpaceFactor(col: number, row: number): number {
    // Count ravers in nearby cells - more crowded = less space satisfaction
    let nearby = 0;
    for (const raver of this.ravers.values()) {
      const dist = Math.abs(raver.col - col) + Math.abs(raver.row - row);
      if (dist <= 2) nearby++;
    }
    // 1-3 ravers nearby is fine, more gets crowded
    if (nearby <= 3) return 1;
    if (nearby >= 8) return 0;
    return 1 - (nearby - 3) / 5;
  }

  destroy(): void {
    for (const sprite of this.sprites.values()) {
      sprite.destroy();
    }
    this.sprites.clear();
    this.ravers.clear();
  }
}
